import { useEffect, useState } from "react";

import { getVenueXPreviewPayload } from "@/data/previewContent";
import { localizedApiHref } from "@/i18n/locale";
import { localizeContent } from "@/i18n/translations";

export type CmsParagraph = {
  html: string;
  text: string;
};

export type CmsContentLink = {
  label: string;
  href: string;
  meta?: string;
};

export type CmsContentFeature = {
  title: string;
  body: string;
};

export type CmsContentMedia = {
  image?: string;
  fallback_url?: string;
  alt?: string;
  position?: string;
};

export type CmsContactFormSettings = {
  title?: string;
  intro?: string;
  submit_label?: string;
  submitting_label?: string;
  success_message?: string;
  name_label?: string;
  company_label?: string;
  email_label?: string;
  phone_label?: string;
  service_label?: string;
  service_placeholder?: string;
  service_options?: string[];
  location_label?: string;
  timeframe_label?: string;
  budget_label?: string;
  message_label?: string;
  referral_label?: string;
  consent_prefix?: string;
  consent_link_label?: string;
};

export type CmsContentPresentation = {
  eyebrow?: string;
  visual?: CmsContentMedia;
  visual_fit?: "cover" | "contain";
  lead_title?: string;
  lead_copy?: string;
  panel_title?: string;
  panel_note?: string;
  panel_body?: string;
  panel_accent?: string;
  hide_hero_intro?: boolean;
  hide_panel_number?: boolean;
  hide_panel_label?: boolean;
  minimal_content?: boolean;
  feature_title?: string;
  feature_copy?: string;
  features?: CmsContentFeature[];
  hide_feature_index?: boolean;
  connected_title?: string;
  connected_copy?: string;
  hide_connected_intro?: boolean;
  route_links?: CmsContentLink[];
  primary_link?: CmsContentLink;
  secondary_link?: CmsContentLink;
  show_contact_form?: boolean;
  privacy_policy_href?: string;
  contact_form?: CmsContactFormSettings;
};

export type CmsContentSection =
  | {
      type: "text";
      heading: string;
      bodyHtml: string;
    }
  | {
      type: "profile";
      heading: string;
      photoUrl: string;
      photoAlt: string;
      name: string;
      role: string;
      bodyHtml: string;
      buttonLabel: string;
      buttonUrl: string;
    };

export type CmsContentPageData = {
  id: number;
  slug: string;
  title: string;
  intro: string;
  seoTitle: string;
  searchDescription: string;
  url: string;
  paragraphs: CmsParagraph[];
  bodyHtml: string;
  sections: CmsContentSection[];
  presentation?: CmsContentPresentation;
};

type CmsContentSectionApiResponse =
  | { type: "text"; heading: string; body_html: string }
  | {
      type: "profile";
      heading: string;
      photo_url: string;
      photo_alt: string;
      name: string;
      role: string;
      body_html: string;
      button_label: string;
      button_url: string;
    };

type CmsContentPageApiResponse = {
  id: number;
  slug: string;
  title: string;
  intro: string;
  seo_title: string;
  search_description: string;
  url: string;
  paragraphs: CmsParagraph[];
  body_html: string;
  sections?: CmsContentSectionApiResponse[];
  presentation?: CmsContentPresentation;
};

function mapSection(section: CmsContentSectionApiResponse): CmsContentSection {
  if (section.type === "profile") {
    return {
      type: "profile",
      heading: section.heading,
      photoUrl: section.photo_url,
      photoAlt: section.photo_alt,
      name: section.name,
      role: section.role,
      bodyHtml: section.body_html,
      buttonLabel: section.button_label,
      buttonUrl: section.button_url,
    };
  }

  return { type: "text", heading: section.heading, bodyHtml: section.body_html };
}

function mapCmsResponse(response: CmsContentPageApiResponse): CmsContentPageData {
  return localizeContent({
    id: response.id,
    slug: response.slug,
    title: response.title,
    intro: response.intro,
    seoTitle: response.seo_title,
    searchDescription: response.search_description,
    url: response.url,
    paragraphs: response.paragraphs,
    bodyHtml: response.body_html,
    sections: (response.sections ?? []).map(mapSection),
    presentation: response.presentation,
  });
}

export function cmsMediaUrl(media: CmsContentMedia | undefined, fallback: string) {
  return media?.image || media?.fallback_url || fallback;
}

export async function fetchCmsContentPage(slug: string): Promise<CmsContentPageData> {
  const response = await fetch(localizedApiHref(`/api/content-pages/${slug}/`), {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load CMS content page: ${slug}`);
  }

  const data = (await response.json()) as CmsContentPageApiResponse;
  return mapCmsResponse(data);
}

export function useCmsContentPage(slug: string, fallback: CmsContentPageData) {
  const previewContent = getVenueXPreviewPayload<CmsContentPageApiResponse>("content-page");
  const [page, setPage] = useState<CmsContentPageData>(() =>
    previewContent ? mapCmsResponse(previewContent) : localizeContent(fallback),
  );

  useEffect(() => {
    if (previewContent) return;

    let cancelled = false;

    fetchCmsContentPage(slug)
      .then((data) => {
        if (!cancelled) {
          setPage(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPage(localizeContent(fallback));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallback, previewContent, slug]);

  return page;
}
